/**
 * Module dependencies.
 */

import { Img, Wrapper } from './image.style';
import { JSX, useEffect, useRef, useState } from 'react';
import { MediaImage } from 'types/media';
import { getAsset } from 'utils/resolve-admin';
import { resolveImageWidth } from 'components/hooks/use-viewport';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  cover?: boolean;
  image: MediaImage | undefined;
  imgStyle?: any;
  onClick?: () => void;
  style?: any;
};

/**
 * `Size` type.
 */

type Size = {
  height: number;
  width: number;
};

/**
 * Export `Image` component.
 */

export function Image(props: Props): JSX.Element {
  const { className, cover, image, imgStyle, onClick, style } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [opacity, setOpacity] = useState(0);
  const [size, setSize] = useState<Size | null>();
  const aspectRatio =
    image?.height && image?.width ? (image.height / image.width) * 100 : 56.25;

  useEffect(() => {
    if (size) {
      return;
    }

    const element = ref.current;

    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height, width } = entry.contentRect;

        setSize({
          height: parseInt(String(height), 10),
          width: parseInt(String(width), 10),
        });
      }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [size]);

  return (
    <Wrapper className={className} onClick={onClick} ref={ref}>
      <div
        style={
          cover
            ? {
                backgroundColor: image?.predominantColor,
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                ...(style ?? {}),
              }
            : {
                backgroundColor: image?.predominantColor,
                paddingTop: `${aspectRatio}%`,
                position: 'relative',
                ...(style ?? {}),
              }
        }
      >
        <Img
          onLoad={() => {
            setOpacity(1);
          }}
          src={
            !size?.width
              ? undefined
              : getAsset(image?.filename_disk, {
                  width: resolveImageWidth(size?.width),
                })
          }
          style={imgStyle}
          visible={opacity > 0}
        />
      </div>
    </Wrapper>
  );
}
